import { useState, Fragment, useEffect, useMemo } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { Modal, Segment, Grid, Header, Divider, List } from 'semantic-ui-react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import SidebarMenu from 'components/Sidebar/SidebarMenu'
import MenuHeader from 'components/Header/Header'
import { actionClick, logout } from 'store/actions/app'
import { initSocketHandlers } from 'utils/socket/chat/handlers'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useTabNavigation, ViewRegistrar } from 'hooks/useTabNavigation'

const PrivateRouteContent = ({
	Component,
	routeProps,
	sidebarOptions,
	isAuthenticated,
	isImpersonating,
	lang,
	user,
	logout,
	actionClick,
	rest,
}) => {
	const { registerActiveView, openTab } = useTabNavigation()
	const {
		setExpand,
		previousExpand,
		setPreviousExpand,
		mobileOnly,
		setMobileOnly,
	} = useLateralMenu()
	const [openModal, setOpenModal] = useState(false)
	const [sidebarMenuOptions, setSidebarMenuOptions] = useState(null)

	const handleSidebarMenuOpen = (options) => setSidebarMenuOptions(options)

	const memoizedRouteProps = useMemo(
		() => ({ ...routeProps, sidebarOptions: sidebarMenuOptions || sidebarOptions }),
		[routeProps, sidebarOptions, sidebarMenuOptions]
	)

	const memoizedLayoutConfig = useMemo(
		() => ({
			Header: MenuHeader,
			Sidebar: SidebarMenu,
			sidebarProps: {
				actionClick,
				user,
				logout,
				setExpand,
				previousExpand,
				setPreviousExpand,
				openModal,
				setOpenModal,
				mobileOnly,
				setMobileOnly,
				lang,
				onMenuOpen: handleSidebarMenuOpen,
				onTabOpen: (route) => openTab(route, routeProps.history.push, false),
			},
			layoutProps: { lang, logout, user, rest },
		}),
		[
			actionClick,
			user,
			logout,
			setExpand,
			previousExpand,
			setPreviousExpand,
			openModal,
			mobileOnly,
			setMobileOnly,
			lang,
			routeProps.history.push,
			openTab,
			rest,
		]
	)

	if (!(isAuthenticated || isImpersonating)) {
		return <Redirect to='/login' />
	}

	return (
		<>
			<ViewRegistrar
				registerActiveView={registerActiveView}
				Component={Component}
				routeProps={memoizedRouteProps}
				layoutConfig={memoizedLayoutConfig}
			/>
			<Modal
				closeIcon
				basic
				centered={true}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				open={openModal}
				size='large'
			>
				<Modal.Content>
					<Segment inverted style={{ height: '100%' }}>
						<Grid stackable divided inverted>
							<Grid.Row columns={2}>
								<Grid.Column width={6} verticalAlign='middle' textAlign='center'>
									<Header inverted content='© Geologistic GPS 2025' />
								</Grid.Column>
								<Grid.Column width={8}>
									<Header inverted content='Contacto' />
									<Divider></Divider>
									<Header
										size='small'
										inverted
										content='¿Necesitas registrar un nuevo ticket o solicitud?'
									/>
									<List divided inverted>
										<List.Item
											header='Ingresa al Portal: '
											content='https://geologistic.com.mx/Soporte/'
											style={{ cursor: 'pointer' }}
											onClick={() => {
												window.open(
													'https://geologistic.com.mx/Soporte/',
													'_blank'
												)
											}}
										/>
										<List.Item
											header='Soporte'
											content='(686) 214 5447, (686) 385 6938,  (686) 510 9915'
										/>
										<List.Item header='Oficina' content='(686) 372 9877' />
										<List.Item
											header='Emergencias'
											content='(686) 420 3333, (686) 214 5447, (686) 385 6938, (686) 510 9915,  (686) 123 6454'
										/>
										<List.Item header='Administración' content='(686) 122 0316' />
										<List.Item
											header='Ventas'
											content='(686) 510 9915, (686) 196 4736, (686) 420 8586,  (686) 191 1571'
										/>
									</List>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</Segment>
				</Modal.Content>
			</Modal>
		</>
	)
}

const PrivateRoute = (props) => {
	const {
		isAuthenticated = true,
		component: Component,
		lang,
		isImpersonating,
		user,
		actionClick,
		...rest
	} = props
	const dispatch = useDispatch()

	useEffect(() => {
		if (!user) return
		dispatch(initSocketHandlers())
	}, [dispatch, user])

	return (
		<Route
			{...rest}
			render={(routeProps) => (
				<PrivateRouteContent
					Component={Component}
					routeProps={routeProps}
					isAuthenticated={isAuthenticated}
					isImpersonating={isImpersonating}
					lang={lang}
					user={user}
					logout={props.logout}
					actionClick={actionClick}
					rest={{ ...rest, user }}
				/>
			)}
		/>
	)
}

const mapDispatchToProps = (dispatch) => ({
	actionClick: (parmas) => dispatch(actionClick(parmas)),
	logout: (option) => dispatch(logout(option)),
})

const mapStateToProps = (state) => ({
	isAuthenticated: !!state.app.user,
	user: state.app.user,
	lang: state.app?.lang,
	isImpersonating: state.app.impersonate_mode,
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute)
